import { combineReducers } from 'redux';
import { push } from 'react-router-redux';
import { call, put, takeLatest } from 'redux-saga/effects';
import message from 'antd/lib/message';
import { t } from '../../../app/i18n';
import {
  createApiActionCreators,
  createActionCreator,
  createReducer,
  createActionType,
  replaceInArray,
  removeFromArray,
  REQUEST,
  SUCCESS,
  FAILURE,
} from '../../../common/utils/reduxHelpers';
import { showModal, hideModal } from '../../../features/modal/ducks';
import { fetchMe } from '../../../features/auth/ducks';
import api, { apiCallIds } from '../api';
import { fetchAnonymizedLoansActions } from '../../loans/ducks';
import config from '../../../config';
import { loanStatus } from '../../../common/enums';

/**
 * ACTION TYPES
 */
export const FETCH_ALLOCATIONS = 'allocations/FETCH_ALLOCATIONS';
export const DELETE_ALLOCATION = 'allocations/DELETE_ALLOCATION';
export const CREATE_ALLOCATION = 'allocations/CREATE_ALLOCATION';
export const SIGN_ALLOCATION = 'allocations/SIGN_ALLOCATION';
export const CONFIRM_ALLOCATION = 'allocations/CONFIRM_ALLOCATION';
export const FETCH_INSTALLMENTS = 'allocations/FETCH_INSTALLMENTS';
export const RESEND_SMS_CODE_ALLOCATION = 'allocations/RESEND_SMS_CODE_ALLOCATION';
export const SET_ALLOCATIONS_FILTER = 'allocations/SET_ALLOCATIONS_FILTER';

/**
 * ACTIONS
 */
export const fetchAllocationsActions = createApiActionCreators(FETCH_ALLOCATIONS);
export const deleteAllocationActions = createApiActionCreators(DELETE_ALLOCATION);
export const createAllocationActions = createApiActionCreators(CREATE_ALLOCATION);
export const signAllocationActions = createApiActionCreators(SIGN_ALLOCATION);
export const confirmAllocationActions = createApiActionCreators(CONFIRM_ALLOCATION);
export const fetchInstallmentsActions = createApiActionCreators(FETCH_INSTALLMENTS);
export const resendSmsCodeAllocationRequest = createActionCreator(RESEND_SMS_CODE_ALLOCATION);
export const setCreditorAllocationsFilterAction = createActionCreator(SET_ALLOCATIONS_FILTER);

/**
 * REDUCERS
 */
const initialState = {
  // allocations: [],
  allocations: {
    items: [],
    filter: {
      status: '',
      type: '',
      createdAt: [],
      interest: [],
      months: []
    },
    totalCount: 0,
    filteredCount: 0
  }
};

const allocations = createReducer(initialState.allocations, {
  [FETCH_ALLOCATIONS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: payload.data,
      filteredCount: payload.total,
      totalCount: payload.trueTotal
    }),
    [FAILURE]: state => ({
      ...state,
      items: []
    }),
  },
  [SET_ALLOCATIONS_FILTER]: (state, payload) => ({
    ...state,
    filter: payload,
  }),
  [DELETE_ALLOCATION]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: removeFromArray(state.items, l => l.id === payload.id)
    }),
  },
  [CREATE_ALLOCATION]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: [payload, ...state.items]}),
  },
  [SIGN_ALLOCATION]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: replaceInArray(state.items, l => l.id === payload.id, payload)
    }),
  },
  [CONFIRM_ALLOCATION]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: replaceInArray(state.items, l => l.id === payload.id, payload)}),
  },
  [FETCH_INSTALLMENTS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: replaceInArray(
      state.items,
      l => l.id === payload.id,
      {
        ...state.items.find(l => l.id === payload.id),
        installments: payload.installments,
      }
    )})
  },
});

export default combineReducers({
  allocations,
});

/**
 * SELECTORS
 */
export const selectAllocationsL = state => state.allocations
export const selectAllocationsState = state => selectAllocationsL(state).allocations;
export const selectAllocations = state => selectAllocationsState(state).items;
export const selectFilteredAllocationsCount = state => selectAllocationsState(state).filteredCount;
export const selectTotalAllocationsCount = state => selectAllocationsState(state).totalCount;
export const selectCreditorAllocationsFilter = state => selectAllocationsState(state).filter
export const selectInstallmentsForAllocation = (state, allocationId) => selectAllocations(state).find(a => a.id === allocationId).installments;

/**
 * SAGAS
 */
function* fetchAllocations({ payload }) {
  console.log(payload)
  const resp = yield call(api.fetchAllocations, payload);

  if (resp.ok) {
    yield put(fetchAllocationsActions.success(resp.data));
  } else {
    yield put(fetchAllocationsActions.failure(resp.error));
  }
}

function* deleteAllocation({ payload }) {
  const resp = yield call(api.deleteAllocation, payload.allocationId);

  if (resp.ok) {
    yield put(deleteAllocationActions.success(resp.data));

    yield fetchMe({
      payload: {
        accountId: payload.accountId,
      },
    });
  } else {
    yield put(deleteAllocationActions.failure(resp.error));
  }
}

function* createAllocation({ payload }) {
  const resp = yield call(api.createAllocation, payload.allocation);

  if (resp.ok) {
    yield put(createAllocationActions.success(resp.data));

    yield put(fetchAnonymizedLoansActions.request({
      append: false,
      options: {
          status: loanStatus.SCH,
          termMonthsMin: '',
          termMonthsMax: '',
          ...config.defaultManualAllocationFilterValues,
          pageNo: 0
        }
      }))
    yield put(
      showModal({
        id: apiCallIds.SIGN_ALLOCATION,
        data: resp.data,
      })
    );

  } else {
    yield put(createAllocationActions.failure(resp.error));
  }
}

function* signAllocation({ payload }) {
  const { allocationId } = payload;

  const resp = yield call(api.signAllocation, allocationId);

  if (resp.ok) {
    yield put(signAllocationActions.success(resp.data));

    yield put(hideModal(apiCallIds.SIGN_ALLOCATION));

    // yield put(
    //   showModal({
    //     id: apiCallIds.CONFIRM_ALLOCATION,
    //     data: resp.data.id,
    //   })
    // );
    yield put(push('/dashboard/overview'))
  } else {
    yield put(signAllocationActions.failure(resp.error));
  }
}

function* confirmAllocation({ payload }) {
  const { allocationId, token } = payload;

  const resp = yield call(api.confirmAllocation, allocationId, token);

  if (resp.ok) {
    yield put(confirmAllocationActions.success(resp.data));
    yield put(hideModal(apiCallIds.CONFIRM_ALLOCATION));
  } else {
    yield put(confirmAllocationActions.failure(resp.error));
  }
}

function* fetchInstallments({ payload }) {
  const resp = yield call(api.fetchInstallments, payload.allocationId);

  if (resp.ok) {
    yield put(fetchInstallmentsActions.success({
      id: payload.allocationId,
      installments: resp.data,
    }));
  } else {
    yield put(fetchInstallmentsActions.failure(resp.error));
  }
}

function* resendSmsCodeAllocation(action) {
  const { allocationId, onSuccess, onError } = action.payload;
  const resp = yield call(api.resendSmsCodeAllocation, allocationId);

  if (resp.ok) {
    message.success(t('SMS kód bol preposlaný'), 3);
    if (onSuccess) {
      onSuccess();
    }
  } else {
    if (onError) {
      onError(resp.error);
    }
  }
}

export function* allocationsSaga() {
  yield takeLatest(createActionType(FETCH_ALLOCATIONS, REQUEST), fetchAllocations);
  yield takeLatest(createActionType(DELETE_ALLOCATION, REQUEST), deleteAllocation);
  yield takeLatest(createActionType(CREATE_ALLOCATION, REQUEST), createAllocation);
  yield takeLatest(createActionType(SIGN_ALLOCATION, REQUEST), signAllocation);
  yield takeLatest(createActionType(CONFIRM_ALLOCATION, REQUEST), confirmAllocation);
  yield takeLatest(createActionType(FETCH_INSTALLMENTS, REQUEST), fetchInstallments);
  yield takeLatest(RESEND_SMS_CODE_ALLOCATION, resendSmsCodeAllocation);
  yield takeLatest(SET_ALLOCATIONS_FILTER, fetchAllocations)
}



// WEBPACK FOOTER //
// ./src/features/allocations/ducks/index.js